import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { TimeoutService } from '@df/ng/timeout.service';
import { ICmsUtilViewContainerDirective } from '@icms/core/util/view-container/icms-util-view-container.directive';
import { NICmsTray } from '../icms-tray.interfaces';
import { ICmsTrayService } from '../icms-tray.service';
import { ICmsTrayItemComponent } from '../item/icms-tray-item.component';
import { Logger } from '@df/core/service/logger';
import { ICMS_CLIENT_CONFIG } from '@icms/client/config/icms-client.config';
import { ICmsEditorConfigService } from '@icms/admin/editor/config/icms-editor-config.service';

@Component({
    standalone: false,
    selector: 'icms-tray-area',
    templateUrl: 'icms-tray-area.html',
    styleUrls: ['icms-tray-area.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ICmsTrayAreaComponent implements OnInit, OnDestroy {
    @ViewChild(ICmsUtilViewContainerDirective, { static: true })
    viewContainerTemplate!: ICmsUtilViewContainerDirective;

    @Input()
    areaId: string = '';

    @Input()
    side: 'left' | 'right' = 'left';

    private currentInstances: NICmsTray.ITrayInstance<any | void, NICmsTray.ITrayBody<any | void>>[] = [];

    @HostBinding('class._hasAny')
    protected hasAny = false;

    private iCmsClientConfig = inject(ICMS_CLIENT_CONFIG);

    private config = inject(ICmsEditorConfigService).config;
    /**
     * Off click tray close
     */
    @HostListener('document:click', ['$event'])
    handleDocumentClick(event: MouseEvent) {
        if (this.config.offClickTrayClose && this.hasAny && event.target && 'closest' in event.target) {
            const target = event.target as HTMLElement;
            if (target.closest('app') && !target.closest('.iCmsTrayOffClickExcluded')) {
                this.closeAll(true, 0);
            }
        }
    }

    constructor(
        private iCmsTrayService: ICmsTrayService,
        private changeDetectorRef: ChangeDetectorRef,
        private timeoutService: TimeoutService,
        private logger: Logger
    ) {}

    add<D, C extends NICmsTray.ITrayBody<D>>(data: NICmsTray.IData<D, C>, options: NICmsTray.IOptions): NICmsTray.ITrayInstance<D, C> {
        if (options.reopenPolicy !== 'both') {
            const existing = (data.component &&
                this.currentInstances.find(
                    instance => instance.data.component === data.component && instance.data.size === data.size
                    // we cant be checking title here as it may change with content data for passData option
                )) as NICmsTray.ITrayInstance<D, C>;
            if (existing) {
                switch (options.reopenPolicy) {
                    case 'last':
                        this.close(existing);
                        break;
                    case 'passData':
                        existing.componentRef.instance.passData(data);
                        return existing;
                    case 'first':
                    default:
                        return existing;
                }
            }
        }

        if (typeof options.position === 'number') {
            const toCloseInstance = this.instances[options.position];
            if (toCloseInstance) {
                this.close(toCloseInstance);
            }
        }

        const viewContainerRef = this.viewContainerTemplate.viewContainerRef;
        const componentRef = viewContainerRef.createComponent<ICmsTrayItemComponent<D, C>>(ICmsTrayItemComponent);
        componentRef.instance.data = data;
        componentRef.instance.componentRef = componentRef;

        const instance: NICmsTray.ITrayInstance<D, C> = {
            componentRef,
            data,
            tray: this
        };
        this.currentInstances.push(instance);
        componentRef.changeDetectorRef.detectChanges();
        componentRef.onDestroy(() => {
            const index = this.currentInstances.indexOf(instance);
            if (index > -1) {
                this.currentInstances.splice(index, 1);
            }
            this.setHasAny(this.currentInstances.length > 0);
            // always close ones on the right
            const nextInstance = this.currentInstances[index];
            if (nextInstance) {
                this.close(nextInstance);
            }
        });
        this.setHasAny(true);
        return instance;
    }

    private setHasAny(hasAny: boolean) {
        if (this.hasAny !== hasAny) {
            this.hasAny = hasAny;
            this.changeDetectorRef.detectChanges();
        }
    }

    closeAll(soft = false, delay = 300) {
        // delay it to let things action - we may want to open another treay and we dont want things to jump around
        this.timeoutService.setTimeout(() => {
            this.iCmsTrayService.closeByArea(this.areaId, soft);
        }, delay); // cannot be longer then in add method of the service
    }

    get instances() {
        return this.currentInstances;
    }

    close<D = any | void, C extends NICmsTray.ITrayBody<D> = any>(instance: NICmsTray.ITrayInstance<D, C>) {
        this.iCmsTrayService.close(instance);
    }

    ngOnInit() {
        if (this.areaId) {
            this.iCmsTrayService.registerArea(this.areaId, this);
        } else {
            this.logger.error('No areaId passed to:', this);
        }
    }

    ngOnDestroy(): void {
        this.iCmsTrayService.unregisterArea(this.areaId);
    }
}
